import type { Frame } from "@/api/spaces";
import type { WsMessage } from "@/api/websocket";
import { getOutboundScreensForContext } from "@/desktop/outbound-screenshot-metadata";
import { useActiveDisplay } from "@/desktop/use-active-display";
import { getWindowRoleSync } from "@/desktop/use-window-role";
import { parseDesiredContentSize } from "@/lib/agent-presentation-frame-size";
import { protectCanvasTimePageGeometry } from "@/lib/canvas-time-page-geometry-guard";
import { suppressNextAutoFitForPresentedFrame } from "@/lib/canvas-viewport-autofit-suppression";
import {
  checkpoint as traceCheckpoint,
  increment as traceIncrement,
} from "@/lib/execution-timer";
import { isTauri } from "@/lib/is-tauri";
import {
  discardFrames,
  ingestFrame,
  queueFramePatch,
} from "@/lib/frame-ingest";
import {
  applyAgentMemoryView,
  type AgentMemoryView,
} from "@/lib/memory-frame-view";
import { presentFramesOrQueue } from "@/lib/pending-frame-presentation";
import { resolveAgentPoint } from "@/lib/pointing-transform";
import { openOtherProjectCardWindow } from "@/desktop/floating-frame-transfer";
import type { CardPresenter } from "@/lib/focus/agent-presented-cards";
import { isOffWallFrameType } from "@/lib/wall-membership";
import { useFramesStore } from "@/stores/frames-store";
import { isWallActive } from "@/stores/workspace-view-store";
import type {
  AvailableModelsMap,
  AvailableRuntime,
} from "@/stores/companion-store";

import type { FrameApplyContext } from "./companion-frame-reducer-shared";
import {
  maybeToastCliUpdates,
  resolveCursorKeyForExecution,
} from "./companion-frame-reducer-shared";
/** Memory frame events: apply an agent view, then refresh the one store graph when asked. */
export function syncMemoryFrameEvent(
  ctx: FrameApplyContext,
  memoryView: AgentMemoryView | undefined,
  { reload }: { reload: boolean },
): void {
  if (memoryView) applyAgentMemoryView(ctx.memoryStore, memoryView);
  if (reload && ctx.ownsSideEffects !== false)
    void ctx.memoryStore.getState().refresh();
}

/**
 * A whole frame for an id this window does not hold, on the wall, of the
 * viewed project. The canvas (and any partial patch) keeps skipping unknown ids.
 */
function isUnknownFullFrameOnWall(
  id: string,
  updates: Partial<Frame>,
  ctx: FrameApplyContext,
): boolean {
  const viewed = ctx.projectStore.getState().currentProject?.id;
  return (
    isWallActive() &&
    (!viewed || !updates.project || updates.project === viewed) &&
    updates.frame_type !== undefined &&
    updates.created_at !== undefined &&
    updates.content !== undefined &&
    !useFramesStore.getState().framesById[id]
  );
}

export function applyFrameEvent(msg: WsMessage, ctx: FrameApplyContext): void {
  switch (msg.type) {
    case "frame.creating": {
      // Legacy servers may still send the old empty placeholder event. Keep
      // it invisible; the complete frame.created payload is the first paint.
      break;
    }
    case "frame.created": {
      const payload = msg.data as unknown as Frame & {
        create_presentation?: Record<string, unknown>;
        auto_show?: boolean;
        memory_view?: AgentMemoryView;
      };
      const {
        create_presentation: createPresentation,
        auto_show: autoShow,
        memory_view: memoryView,
        ...frameData
      } = payload;
      const presentation =
        msg.device_local === false
          ? undefined
          : (createPresentation ??
            (autoShow
              ? {
                  context: "canvas",
                  visibility: "show_only",
                }
              : undefined));
      const frame = (
        msg.device_local !== false && autoShow && frameData.metadata?.hidden
          ? {
              ...frameData,
              metadata: { ...frameData.metadata, hidden: false },
            }
          : frameData
      ) as Frame;
      const fxId =
        (msg.data as { execution_id?: string }).execution_id ??
        (msg.data as { metadata?: { execution_id?: string } }).metadata
          ?.execution_id;
      if (fxId) {
        traceCheckpoint(fxId, "first_frame_rendered", true);
        traceIncrement(fxId, "ws_event_count");
      }
      // Denker files cards in the project the work belongs to. One it presents
      // from another project is held in the store, so its updates reach the
      // window or Focus showing it, but the wall lists only the viewed
      // project's cards (`selectWallFrameIds`); an unpresented one stays out.
      const viewed = ctx.projectStore.getState().currentProject?.id;
      const elsewhere =
        !!viewed &&
        !!frame.project &&
        frame.project !== viewed &&
        !isOffWallFrameType(frame.frame_type);
      const exists = !!useFramesStore.getState().framesById[frame.id];
      if (exists) {
        queueFramePatch(
          frame.id,
          protectCanvasTimePageGeometry(frame.id, frame),
        );
      } else if (!elsewhere || presentation) {
        const actualStatus = frame.status;
        if (presentation || msg.device_local === false) {
          suppressNextAutoFitForPresentedFrame(frame.id);
        }
        ingestFrame({ ...frame, status: "streaming" });
        ctx.defer(() => {
          queueFramePatch(frame.id, { status: actualStatus });
        }, 1000);
      }
      // On the desktop it opens at once in its own window (the owner opens it
      // once; it reaches every display); on the web Focus shows it at once.
      const shownElsewhere =
        elsewhere &&
        !!presentation &&
        (ctx.ownsSideEffects === false
          ? isTauri()
          : openOtherProjectCardWindow(frame, presenterOf(presentation)));
      if (presentation && !shownElsewhere) {
        presentCreatedFrameOnCanvas(
          {
            ...presentation,
            space_id: frame.project ?? frame.space,
            frame_ids: [frame.id],
          },
          ctx,
        );
      }
      // memory_graph's singleton frame; a newly mounted graph loads itself.
      if (frame.frame_type === "memory") {
        syncMemoryFrameEvent(ctx, memoryView, { reload: exists });
      }
      break;
    }
    case "frame.updated": {
      const {
        id,
        memory_view: memoryView,
        ...updates
      } = msg.data as {
        id: string;
        frame_type?: string;
        memory_view?: AgentMemoryView;
      } & Partial<Frame>;
      // memory_view is a one-shot agent request, never frame data.
      if (isUnknownFullFrameOnWall(id, updates, ctx)) {
        // Unarchived or moved here from another window: the card comes back.
        ingestFrame({ ...updates, id } as Frame);
      } else {
        queueFramePatch(id, protectCanvasTimePageGeometry(id, updates));
      }
      if (updates.frame_type === "memory") {
        syncMemoryFrameEvent(ctx, memoryView, { reload: true });
      }
      break;
    }
    case "frame.deleted": {
      const deletedId = (msg.data.id ?? msg.data.frame_id) as string;
      if (deletedId) discardFrames([deletedId]);
      break;
    }
  }
}

export function stringArray(value: unknown): string[] | undefined {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === "string")
    : undefined;
}

export function screenshotContextFromData(data: Record<string, unknown>) {
  return {
    executionId:
      typeof data.execution_id === "string" ? data.execution_id : undefined,
    sessionId:
      typeof data.runtime_session_id === "string"
        ? data.runtime_session_id
        : undefined,
    imageIds: stringArray(data.image_ids),
  };
}

export function displayIdFromDesktopPayload(
  raw: Record<string, unknown>,
): number | null {
  if (typeof raw.display_id === "number") return raw.display_id;
  if (typeof raw.target_display_id === "number") return raw.target_display_id;
  if (typeof raw.screen_id === "number") return raw.screen_id;
  return null;
}

export function resolvePresentationCursorPoint(data: Record<string, unknown>) {
  const rawPoint = data.cursor_point as Record<string, unknown> | undefined;
  const cursorPoint =
    rawPoint && typeof rawPoint.x === "number" && typeof rawPoint.y === "number"
      ? {
          x: rawPoint.x,
          y: rawPoint.y,
          displayId: displayIdFromDesktopPayload(rawPoint),
        }
      : null;
  const rawScreenshotPoint = data.screenshot_point as
    | Record<string, unknown>
    | undefined;
  const resolvedScreenshotPoint =
    rawScreenshotPoint &&
    typeof rawScreenshotPoint.screen === "number" &&
    typeof rawScreenshotPoint.x === "number" &&
    typeof rawScreenshotPoint.y === "number"
      ? resolveAgentPoint(
          {
            screen: rawScreenshotPoint.screen,
            x: rawScreenshotPoint.x,
            y: rawScreenshotPoint.y,
          },
          getOutboundScreensForContext(screenshotContextFromData(data)),
        )
      : null;

  return (
    cursorPoint ??
    (resolvedScreenshotPoint
      ? {
          x: resolvedScreenshotPoint.globalX,
          y: resolvedScreenshotPoint.globalY,
          displayId: resolvedScreenshotPoint.displayId,
        }
      : null)
  );
}

export function hasAgentPresentationContext(
  data: Record<string, unknown>,
): boolean {
  return (
    typeof data.agent_id === "string" ||
    typeof data.execution_id === "string" ||
    typeof data.name === "string"
  );
}

export function resolvePresentationAgentCursor(
  data: Record<string, unknown>,
  ctx: FrameApplyContext,
) {
  const directAgentId =
    typeof data.agent_id === "string" ? data.agent_id : undefined;
  if (directAgentId) {
    const direct = ctx.canvasStore.getState().agentCursors.get(directAgentId);
    if (direct) return direct;
  }

  const executionId =
    typeof data.execution_id === "string" ? data.execution_id : undefined;
  const spaceId =
    typeof data.space_id === "string"
      ? data.space_id
      : ctx.projectStore.getState().currentSpace?.id;
  const cursorKey = resolveCursorKeyForExecution(ctx, executionId, spaceId);
  return cursorKey
    ? ctx.canvasStore.getState().agentCursors.get(cursorKey)
    : undefined;
}

const stringOf = (value: unknown) =>
  typeof value === "string" ? value : undefined;

/** Who presents a created frame: its agent, execution and owner stream. */
function presenterOf(data: Record<string, unknown>): CardPresenter {
  return {
    agentId: stringOf(data.agent_id),
    executionId: stringOf(data.execution_id),
    ownerId: stringOf(data.owner_id),
  };
}

export function presentCreatedFrameOnCanvas(
  data: Record<string, unknown>,
  ctx: FrameApplyContext,
): void {
  const frameIds = Array.isArray(data.frame_ids)
    ? data.frame_ids.filter((id): id is string => typeof id === "string")
    : [];
  if (frameIds.length === 0) return;
  const context =
    data.context === "screenshot" || data.context === "canvas"
      ? data.context
      : "canvas";
  const placementValues = new Set<string>([
    "center",
    "auto",
    "left",
    "right",
    "top",
    "bottom",
    "cursor",
  ]);
  const placement = (
    typeof data.placement === "string" && placementValues.has(data.placement)
      ? data.placement
      : "center"
  ) as "center" | "auto" | "left" | "right" | "top" | "bottom" | "cursor";
  const visibilityValues = new Set<string>([
    "show_only",
    "show_selected",
    "keep",
  ]);
  const visibility =
    typeof data.visibility === "string" && visibilityValues.has(data.visibility)
      ? (data.visibility as "show_only" | "show_selected" | "keep")
      : undefined;

  const hasAgentContext = hasAgentPresentationContext(data);
  const cursorPoint = resolvePresentationCursorPoint(data);
  const agentCursor = hasAgentContext
    ? resolvePresentationAgentCursor(data, ctx)
    : undefined;
  const desiredSize = parseDesiredContentSize(data.desired_size);
  presentFramesOrQueue({
    frameIds,
    context,
    placement,
    visibility,
    cursorPoint,
    targetDisplayId:
      typeof data.target_display_id === "number"
        ? data.target_display_id
        : (cursorPoint?.displayId ??
          agentCursor?.displayId ??
          (hasAgentContext
            ? useActiveDisplay.getState().activeDisplayId
            : null)),
    cursorLabel:
      typeof data.cursor_label === "string" ? data.cursor_label : undefined,
    agentId: typeof data.agent_id === "string" ? data.agent_id : undefined,
    agentName: typeof data.name === "string" ? data.name : undefined,
    agentColor: typeof data.color === "string" ? data.color : undefined,
    spaceId: typeof data.space_id === "string" ? data.space_id : undefined,
    executionId:
      typeof data.execution_id === "string" ? data.execution_id : undefined,
    ownerId: typeof data.owner_id === "string" ? data.owner_id : undefined,
    desiredContentSize: desiredSize
      ? {
          width: desiredSize.width,
          height: desiredSize.height,
          aspectRatio: desiredSize.aspect_ratio,
        }
      : undefined,
  });
}

export function hasAnyAvailableModels(models: AvailableModelsMap): boolean {
  return Object.values(models).some(
    (runtimeModels) => Array.isArray(runtimeModels) && runtimeModels.length > 0,
  );
}

export function applyCompanionStatusEvent(
  msg: WsMessage,
  ctx: FrameApplyContext,
): void {
  switch (msg.type) {
    case "companion.status": {
      const msgKind = msg.data.kind as string | undefined;
      const currentKind = isTauri() ? "desktop" : "web";
      if (msgKind && msgKind !== currentKind) return;
      const isConnected = (msg.data.status as string) === "connected";
      ctx.companionStore
        .getState()
        .setStatus(isConnected ? "connected" : "disconnected");
      if (isConnected) ctx.companionStore.getState().setHasToken(true);
      const port = msg.data.event_port as number | undefined;
      ctx.companionStore.getState().setEventPort(port ?? null);
      if (msg.data.available_runtimes) {
        const runtimes = msg.data.available_runtimes as AvailableRuntime[];
        ctx.companionStore
          .getState()
          .setAvailableRuntimes(
            runtimes,
            (msg.data.default_runtime as string) ?? "claude_cli",
          );
        if (!isTauri()) maybeToastCliUpdates(runtimes, ctx);
      }
      if (msg.data.available_models) {
        const models = msg.data.available_models as AvailableModelsMap;
        if (hasAnyAvailableModels(models)) {
          ctx.companionStore.getState().setAvailableModels(models);
        }
      }
      break;
    }
    case "desktop.update_available":
      // Fanout from the desktop-release CI webhook. Browser tabs ignore
      // this — they refresh on their own SPA cycle. The Tauri owner
      // webview re-emits it as a local Tauri event that useAutoUpdate
      // listens to and translates into an immediate check() call. The
      // hook also filters by channel (preview vs prod) so cross-channel
      // notifications are no-ops on the wrong build.
      // Owner-only: mirrors run this same reducer via the owner's
      // mirror-event rebroadcast, and Tauri emit() is global across
      // webviews — without the gate, one WS message would fire the
      // owner's listener once per open window.
      if (isTauri() && getWindowRoleSync() === "owner") {
        const channel = (msg.data.channel as string | undefined) ?? "";
        const version = (msg.data.version as string | undefined) ?? "";
        void import("@tauri-apps/api/event")
          .then(({ emit }) =>
            emit("denker://desktop-update-available", { channel, version }),
          )
          .catch(() => undefined);
      }
      break;
  }
}
